import React, { useState, useEffect, useCallback } from 'react';
import { getComments, createComment, toggleLikeComment, updateComment, deleteComment } from '../../../services/CommentServices';
import CommentForm from './CommentForm';
import CommentItem from './CommentItem';
import styles from './styles/ScenarioComments.module.css';

const ScenarioComments = ({ originId, onModel = 'Scenario', currentUser }) => {
    const COMMENTS_PER_PAGE = 10;

    const [comments, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);

    const fetchComments = useCallback(async (pageNum) => {
        setLoading(true);
        try {
            const newComments = await getComments(originId, pageNum, COMMENTS_PER_PAGE);

            setComments(prev => {
                if (pageNum === 1) return newComments;
                
                const existingIds = new Set(prev.map(c => c._id));
                const uniqueNewComments = newComments.filter(c => !existingIds.has(c._id));
                return [...prev, ...uniqueNewComments];
            });

            if (newComments.length < COMMENTS_PER_PAGE) {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Error fetching comments:", err);
        } finally {
            setLoading(false);
        }
    }, [originId]);

    useEffect(() => {
        setComments([]);
        setPage(1);
        setHasMore(true);
        fetchComments(1);
    }, [originId, fetchComments]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchComments(nextPage);
    };

    const handleCreateComment = async (message) => {
        if (!currentUser) return alert("Please log in.");
        
        const data = await createComment(originId, message, onModel);
        setComments(prev => [data, ...prev]);
    };

    const handleToggleLike = async (commentId) => {
        await toggleLikeComment(commentId);
    };

    const handleUpdateComment = async (commentId, newMessage) => {
        const data = await updateComment(commentId, newMessage);
        setComments(prev => prev.map(c =>
            c._id === commentId ? { ...c, message: data.message } : c
        ));
    };

    const handleDeleteComment = async (commentId) => {
        await deleteComment(commentId);

        setComments(prev => prev.filter(c => c._id !== commentId));
    };

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Comments</h3>

            {currentUser ? (
                <div className={styles.formWrapper}>
                    <CommentForm onSubmit={handleCreateComment} />
                </div>
            ) : (
                <div className={styles.loginPrompt}>
                    <p>Please log in to post a comment.</p>
                </div>
            )}

            <div className={styles.commentsList}>
                {comments.map(comment => (
                    <CommentItem 
                        key={comment._id} 
                        comment={comment} 
                        currentUser={currentUser}
                        onLike={handleToggleLike}
                        onUpdate={handleUpdateComment}
                        onDelete={handleDeleteComment}
                    />
                ))}
            </div>

            <div className={styles.footerActions}>
                {loading && <p className={styles.statusText}>Loading comments...</p>}
                
                {!loading && hasMore && comments.length > 0 && (
                    <button onClick={handleLoadMore} className={styles.loadMoreButton}>
                        Load More
                    </button>
                )}
                
                {!loading && comments.length === 0 && (
                    <p className={styles.statusText}>No comments yet. Be the first!</p>
                )}

                {!loading && !hasMore && comments.length > 0 && (
                    <p className={styles.endText}>End of comments.</p>
                )}
            </div>
        </div>
    );
};

export default ScenarioComments;
